import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bug, Download, RefreshCw, Server, Database, Clock, 
  AlertTriangle, CheckCircle, XCircle, Activity, 
  ChevronDown, ChevronRight, FileJson, Loader2,
  Play, Pause, ArrowRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DebugViewer = () => {
  const [debugData, setDebugData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    jobs: true,
    database: true,
    errors: true,
    architecture: false,
    config: false
  });

  const fetchDebugData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/debug/system-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setDebugData(data);
    } catch (error) {
      console.error('Debug fetch error:', error);
      toast.error('Fehler beim Laden der Debug-Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDebugData();
  }, [fetchDebugData]);

  // Auto-Refresh alle 10 Sekunden wenn aktiviert
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchDebugData, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchDebugData]);

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/debug/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug_report_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Debug-Report heruntergeladen');
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-green-400 bg-green-400/20';
      case 'completed': return 'text-blue-400 bg-blue-400/20';
      case 'stopped': return 'text-amber-400 bg-amber-400/20';
      case 'failed': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString('de-DE');
    } catch {
      return ts;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bug className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-xl font-bold text-white">System Debug & Logs</h1>
              <p className="text-gray-400 text-sm">
                Generiert: {formatTimestamp(debugData?.generated_at)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Auto-Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${
                autoRefresh 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              Auto-Refresh {autoRefresh ? 'AN' : 'AUS'}
            </button>
            
            {/* Manual Refresh */}
            <button
              onClick={() => { setLoading(true); fetchDebugData(); }}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Aktualisieren
            </button>
            
            {/* Download */}
            <button
              onClick={downloadReport}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Report herunterladen
            </button>
          </div>
        </div>

        {/* Laufende Jobs - WICHTIGSTER BEREICH */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('jobs')}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50"
          >
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-green-400" />
              <span className="text-white font-medium">Laufende Jobs</span>
              {debugData?.jobs?.running?.length > 0 && (
                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">
                  {debugData.jobs.running.length} aktiv
                </span>
              )}
            </div>
            {expandedSections.jobs ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          
          {expandedSections.jobs && (
            <div className="p-4 border-t border-gray-700 space-y-4">
              {debugData?.jobs?.running?.length > 0 ? (
                debugData.jobs.running.map((job) => (
                  <div key={job.id} className="bg-gray-900 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                        <span className="text-white font-medium">{job.type}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                      <span className="text-gray-400 text-sm">ID: {job.id?.slice(0, 8)}...</span>
                    </div>
                    
                    {/* Progress */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-400 text-xs">Fortschritt</p>
                        <p className="text-white font-mono">
                          {job.vehicles_processed} / {job.total_vehicles}
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-400 text-xs">Aktuelles Fahrzeug</p>
                        <p className="text-cyan-400 font-mono text-sm truncate">
                          {job.current_vehicle || '-'}
                        </p>
                      </div>
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-400 text-xs">Gestartet</p>
                        <p className="text-white text-sm">{formatTimestamp(job.started_at)}</p>
                      </div>
                      <div className="bg-gray-800 rounded p-2">
                        <p className="text-gray-400 text-xs">Fehler</p>
                        <p className={`font-mono ${job.errors?.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {job.errors?.length || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Log Entries */}
                    {job.last_log_entries?.length > 0 && (
                      <div className="bg-gray-800 rounded p-3 max-h-48 overflow-y-auto">
                        <p className="text-gray-400 text-xs mb-2">Letzte Log-Einträge:</p>
                        <div className="space-y-1 font-mono text-xs">
                          {job.last_log_entries.map((log, i) => (
                            <div key={i} className="text-gray-300">{log}</div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Errors */}
                    {job.errors?.length > 0 && (
                      <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                        <p className="text-red-400 text-xs mb-2">Fehler:</p>
                        <div className="space-y-1 font-mono text-xs text-red-300">
                          {job.errors.map((err, i) => (
                            <div key={i}>{err}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Keine laufenden Jobs</p>
                </div>
              )}
              
              {/* Recent Jobs */}
              {debugData?.jobs?.recent?.length > 0 && (
                <div className="mt-4">
                  <p className="text-gray-400 text-sm mb-2">Letzte abgeschlossene Jobs:</p>
                  <div className="space-y-2">
                    {debugData.jobs.recent.slice(0, 5).map((job) => (
                      <div key={job.id} className="bg-gray-900 rounded p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {job.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-400" />}
                          {job.status === 'stopped' && <XCircle className="w-4 h-4 text-amber-400" />}
                          {job.status === 'failed' && <AlertTriangle className="w-4 h-4 text-red-400" />}
                          <span className="text-white text-sm">{job.type}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        </div>
                        <div className="text-gray-400 text-sm">
                          {job.vehicles_processed} Fahrzeuge • {job.fields_collected} Felder
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Datenbank Stats - ERWEITERT */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('database')}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50"
          >
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">Datenbank-Statistiken</span>
            </div>
            {expandedSections.database ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          
          {expandedSections.database && debugData?.database_stats && (
            <div className="p-4 border-t border-gray-700 space-y-6">
              
              {/* Basis-Statistiken */}
              <div>
                <h4 className="text-gray-400 text-sm mb-3">📊 Basis-Daten</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <StatCard 
                    label="Fahrzeuge (Master)" 
                    value={debugData.database_stats.vehicle_master_data?.total || 0} 
                    color="cyan"
                  />
                  <StatCard 
                    label="Mit Specs" 
                    value={debugData.database_stats.vehicle_master_data?.with_specs || 0} 
                    color="green"
                  />
                  <StatCard 
                    label="Mit Tuning" 
                    value={debugData.database_stats.vehicle_master_data?.with_tuning || 0} 
                    color="purple"
                  />
                  <StatCard 
                    label="Mit Bild" 
                    value={debugData.database_stats.vehicle_master_data?.with_image || 0} 
                    color="pink"
                  />
                  <StatCard 
                    label="Mit Reparaturen" 
                    value={debugData.database_stats.vehicle_master_data?.with_repair_guides || 0} 
                    color="amber"
                  />
                  <StatCard 
                    label="Reparaturen Gesamt" 
                    value={debugData.database_stats.vehicle_master_data?.total_repair_guides_count || 0} 
                    color="amber"
                  />
                </div>
              </div>
              
              {/* Reparatur-Videos & Advanced */}
              <div>
                <h4 className="text-gray-400 text-sm mb-3">🎥 Reparaturen & Videos</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <StatCard 
                    label="Mit Rep-Videos" 
                    value={debugData.database_stats.vehicle_master_data?.with_repair_videos || 0} 
                    color="red"
                  />
                  <StatCard 
                    label="Mit Advanced Data" 
                    value={debugData.database_stats.vehicle_master_data?.with_advanced_data || 0} 
                    color="blue"
                  />
                </div>
              </div>
              
              {/* Advanced Data Breakdown */}
              {debugData.database_stats.vehicle_master_data?.advanced_breakdown && (
                <div>
                  <h4 className="text-gray-400 text-sm mb-3">🔧 Advanced Data (Detailliert)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <StatCard 
                      label="Diagnose/DTC" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.diagnostics_dtc || 0} 
                      color="cyan"
                    />
                    <StatCard 
                      label="Service-Intervalle" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.service_intervals || 0} 
                      color="green"
                    />
                    <StatCard 
                      label="Elektrik" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.electrical || 0} 
                      color="yellow"
                    />
                    <StatCard 
                      label="Teilenummern" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.parts_numbers || 0} 
                      color="purple"
                    />
                    <StatCard 
                      label="Mechanik" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.mechanical || 0} 
                      color="blue"
                    />
                    <StatCard 
                      label="Räder/TPMS" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.wheels_tpms || 0} 
                      color="amber"
                    />
                    <StatCard 
                      label="Schwachstellen" 
                      value={debugData.database_stats.vehicle_master_data.advanced_breakdown?.failure_modes || 0} 
                      color="red"
                    />
                  </div>
                </div>
              )}
              
              {/* Tuning Breakdown */}
              {debugData.database_stats.vehicle_master_data?.tuning_breakdown && (
                <div>
                  <h4 className="text-gray-400 text-sm mb-3">⚡ Tuning & Hardware</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard 
                      label="Mit ECU-Info" 
                      value={debugData.database_stats.vehicle_master_data.tuning_breakdown?.with_ecu_info || 0} 
                      color="cyan"
                    />
                    <StatCard 
                      label="Mit Hardware-Methoden" 
                      value={debugData.database_stats.vehicle_master_data.tuning_breakdown?.with_hardware_methods || 0} 
                      color="purple"
                    />
                  </div>
                </div>
              )}
              
              {/* Weitere Daten */}
              {debugData.database_stats.vehicle_master_data?.other_data && (
                <div>
                  <h4 className="text-gray-400 text-sm mb-3">📋 Weitere Fahrzeugdaten</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    <StatCard 
                      label="Klimaanlage" 
                      value={debugData.database_stats.vehicle_master_data.other_data?.ac_data || 0} 
                      color="blue"
                    />
                    <StatCard 
                      label="Drehmomente" 
                      value={debugData.database_stats.vehicle_master_data.other_data?.torque_specs || 0} 
                      color="amber"
                    />
                    <StatCard 
                      label="Positionen" 
                      value={debugData.database_stats.vehicle_master_data.other_data?.component_positions || 0} 
                      color="green"
                    />
                    <StatCard 
                      label="Sicherungen" 
                      value={debugData.database_stats.vehicle_master_data.other_data?.fuses || 0} 
                      color="yellow"
                    />
                    <StatCard 
                      label="Steuergeräte" 
                      value={debugData.database_stats.vehicle_master_data.other_data?.ecus_info || 0} 
                      color="purple"
                    />
                    <StatCard 
                      label="Batterie" 
                      value={debugData.database_stats.vehicle_master_data.other_data?.battery || 0} 
                      color="cyan"
                    />
                  </div>
                </div>
              )}
              
              {/* Katalog-Info */}
              <div>
                <h4 className="text-gray-400 text-sm mb-3">📚 Katalog & Quellen</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <StatCard 
                    label="Hersteller im Katalog" 
                    value={debugData.database_stats.vehicle_catalog_v2?.total || 0} 
                    color="cyan"
                  />
                  <StatCard 
                    label="vehicle_data (Legacy)" 
                    value={debugData.database_stats.vehicle_data?.total || 0} 
                    color="gray"
                  />
                </div>
              </div>
              
            </div>
          )}
        </div>

        {/* Fehler */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('errors')}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-white font-medium">Letzte Fehler</span>
              {debugData?.recent_errors?.length > 0 && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">
                  {debugData.recent_errors.length}
                </span>
              )}
            </div>
            {expandedSections.errors ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          
          {expandedSections.errors && (
            <div className="p-4 border-t border-gray-700">
              {debugData?.recent_errors?.length > 0 ? (
                <div className="space-y-2">
                  {debugData.recent_errors.map((err, i) => (
                    <div key={i} className="bg-red-900/20 border border-red-500/30 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-red-400 text-sm font-medium">{err.job_type}</span>
                        <span className="text-gray-500 text-xs">{err.job_id?.slice(0, 8)}...</span>
                      </div>
                      <p className="text-red-300 text-sm font-mono">{err.error}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400 opacity-50" />
                  <p>Keine aktuellen Fehler</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* System-Architektur */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('architecture')}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50"
          >
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">System-Architektur</span>
            </div>
            {expandedSections.architecture ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          
          {expandedSections.architecture && debugData?.system_architecture && (
            <div className="p-4 border-t border-gray-700">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Backend */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <h4 className="text-cyan-400 font-medium mb-3 flex items-center gap-2">
                    <Server className="w-4 h-4" />
                    Backend
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-400">Framework: <span className="text-white">{debugData.system_architecture.backend.framework}</span></p>
                    <p className="text-gray-400">Port: <span className="text-white">{debugData.system_architecture.backend.port}</span></p>
                    <div className="mt-3">
                      <p className="text-gray-400 mb-1">Key Services:</p>
                      <ul className="space-y-1">
                        {debugData.system_architecture.backend.key_services.map((s, i) => (
                          <li key={i} className="text-gray-300 text-xs flex items-start gap-1">
                            <ArrowRight className="w-3 h-3 mt-0.5 text-cyan-400 flex-shrink-0" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Database Collections */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <h4 className="text-blue-400 font-medium mb-3 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Datenbank-Collections
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(debugData.system_architecture.database_collections).map(([key, desc]) => (
                      <div key={key} className="flex items-start gap-2">
                        <code className="text-cyan-400 text-xs bg-gray-800 px-1 rounded">{key}</code>
                        <span className="text-gray-400 text-xs">{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Raw JSON */}
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileJson className="w-5 h-5 text-gray-400" />
              <span className="text-white font-medium">Raw JSON</span>
            </div>
          </div>
          <pre className="bg-gray-900 rounded p-4 text-xs text-gray-300 overflow-auto max-h-96 font-mono">
            {JSON.stringify(debugData, null, 2)}
          </pre>
        </div>

      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, color }) => {
  const colors = {
    cyan: 'text-cyan-400',
    green: 'text-green-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
    blue: 'text-blue-400',
    pink: 'text-pink-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    gray: 'text-gray-400'
  };
  
  return (
    <div className="bg-gray-900 rounded-lg p-3">
      <p className="text-gray-400 text-xs mb-1">{label}</p>
      <p className={`text-2xl font-bold ${colors[color] || 'text-white'}`}>{value}</p>
    </div>
  );
};

export default DebugViewer;
